import '../App.css';
import React, { useState, useEffect } from 'react';
import avtaar from '../images/Charactor-1.jpeg';
import Spline from '@splinetool/react-spline';
import scroll from '../images/scroll-top-img.png';
import SmoothScrollingLink from './SmoothScrollingLink';
import Works from './Works';
import Skills from './Skills';
import Experience from './Experience';
import Contact from './Contact';
import SplashCursor from './SplashCursor';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import Lenis from '@studio-freight/lenis';

gsap.registerPlugin(ScrollTrigger);

function Home({ onSplineLoad }) {
  const [btnClass, setBtnClass] = useState('');

  const handleClick = () => {
    setBtnClass('btn-progress');

    setTimeout(() => {
      setBtnClass('btn-progress btn-fill');
    }, 500);

    setTimeout(() => {
      setBtnClass('btn-complete');
    }, 4100);

    setTimeout(() => {
      setBtnClass('');
    }, 4100);
  };

  useEffect(() => {
    const lenis = new Lenis({
      lerp: 0.07,
    });

    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });
    gsap.ticker.lagSmoothing(0);

    // Navbar animation
    gsap.from('.navbar li', {
      y: -100,
      opacity: 0,
      duration: 1,
      stagger: 0.2,
      ease: 'power3.out',
      delay: 0.5,
    });

    // Hero text animation
    gsap.from('.hero-text h1', {
      y: 50,
      opacity: 0,
      duration: 1.2,
      ease: 'power3.out',
      delay: 0.8,
    });

    gsap.from('.hero-text h3', {
      y: 30,
      opacity: 0,
      duration: 1,
      ease: 'power3.out',
      delay: 1.2,
    });

    // Social ribbon animation
    gsap.from('.ribbon .links', {
      x: -50,
      opacity: 0,
      duration: 0.8,
      stagger: 0.15,
      ease: 'power3.out',
      delay: 1.2,
    });

    // About section animations
    gsap.from('.about-heading', {
      scrollTrigger: {
        trigger: '.about',
        start: 'top 80%',
        end: 'top 40%',
        scrub: 1,
      },
      x: 200,
      opacity: 0,
    });

    gsap.from('.about-heading-decoration', {
      scrollTrigger: {
        trigger: '.about',
        start: 'top 70%',
        end: 'top 35%',
        scrub: 1,
      },
      scaleX: 0,
      transformOrigin: 'right center',
    });

    gsap.from('.avtaar', {
      scrollTrigger: {
        trigger: '.info',
        start: 'top 80%',
        end: 'top 50%',
        scrub: 1,
      },
      scale: 0.5,
      opacity: 0,
      rotation: -10,
    });

    gsap.from('.myself', {
      scrollTrigger: {
        trigger: '.info',
        start: 'top 80%',
        end: 'top 50%',
        scrub: 1,
      },
      x: 100,
      opacity: 0,
    });

    return () => {
      lenis.destroy();
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <>
      {/* Fluid cursor effect */}
      <SplashCursor />

      <div className='container'>
        {/* social links */}
        <div className='ribbon'>
          <ul className='list'>
            <li className='links'>
              <a href='https://github.com/ItzzAkib07' target='_blank' rel='noreferrer'>
                <i className="fa-brands fa-github"></i>
              </a>
            </li>
            <li className='links'>
              <a href='https://www.linkedin.com/in/akib-mulla-931860194/' target='_blank' rel='noreferrer'>
                <i className="fa-brands fa-linkedin"></i>
              </a>
            </li>
            <li className='links'>
              <a href='https://www.instagram.com/itzz._akib._____/' target='_blank' rel='noreferrer'>
                <i className="fa-brands fa-instagram"></i>
              </a>
            </li>
            <li className='links'>
              <a href='https://www.facebook.com/akib.mulla.961' target='_blank' rel='noreferrer'>
                <i className="fa-brands fa-facebook"></i>
              </a>
            </li>
          </ul>
        </div>

        {/* Home section */}
        <section className='home' id='home'>
          <nav>
            <ul className='navbar'>
              <SmoothScrollingLink to="home">
                <li className='nav-link nav-link-ltr'>Home</li>
              </SmoothScrollingLink>

              <SmoothScrollingLink to="about">
                <li className='nav-link nav-link-ltr'>About</li>
              </SmoothScrollingLink>

              <SmoothScrollingLink to="works">
                <li className='nav-link nav-link-ltr'>My Work</li>
              </SmoothScrollingLink>

              <SmoothScrollingLink to="experience">
                <li className='nav-link nav-link-ltr'>Experience</li>
              </SmoothScrollingLink>

              <SmoothScrollingLink to="contact">
                <li className='nav-link nav-link-ltr'>Contact</li>
              </SmoothScrollingLink>
            </ul>
          </nav>

          <div className='hero-text'>
            <h1>Hi, I'm <span className='name-highlight'>AKIB</span></h1>
            <h3>Frontend Developer</h3>
          </div>

          <div className='spline-container'>
            <Spline scene="/charactor.splinecode" className='bg' onLoad={onSplineLoad} />
          </div>
        </section>

        {/* About section */}
        <section className='about' id='about'>
          <div className='spline-viewer'>
            <Spline scene="/about.splinecode" className='about-spline' />
          </div>

          <div className='about-heading'>
            <div className='about-heading-line'>
              <span className='about-heading-decoration'></span>
              <h1 className='about-title'>Hello</h1>
            </div>
            <p className='sub-title'>There<span className='accent-dot'>.</span></p>
          </div>

          <div className='info'>
            <div className='avtaar'>
              <img src={avtaar} alt='avatar' />
            </div>

            <div className='myself'>
              <span>
                I'm on a mission to transform imagination into innovation. As a web developer, I'm not just a code junkie; I'm a dream weaver, a digital architect, and a problem solver 🪄 ✨
                <br />
                My coding toolbox is filled with the latest technologies and frameworks, including HTML5, CSS3, JavaScript, React.js, Material UI and more. I'm always learning and evolving to stay on the cutting edge of web development.
              </span>

              <a href={avtaar} className={`btn ${btnClass}`} onClick={handleClick} download>
                Download Resume <i className="fa-solid fa-download"></i>
              </a>
            </div>
          </div>
        </section>

        {/* Skills section */}
        <Skills />

        {/* Work section */}
        <Works />

        {/* Experience section */}
        <Experience />

        {/* Contact section */}
        <Contact />

        {/* Scroll Top button */}
        <div className='scroll-wrapper'>
          <SmoothScrollingLink to="home">
            <span className='scroll'>
              <button>
                <img src={scroll} alt='scroll to top' />
              </button>
            </span>
          </SmoothScrollingLink>
        </div>
      </div>
    </>
  );
}

export default Home;
